import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { Button as ButtonUnstyled } from '@mui/base/Button';
import cx from 'classnames';
import { Box, Stack } from 'src/components-v2/Layout';
import { useTrack } from 'src/contexts/AnalyticsContext';
import { Events } from 'src/lib/trackingEvents';
import styled from 'threads5/themes/styled';
import { IBtnProps } from './Btn.types';
import { useLocalizedHref } from './useLocalizedHref';

const StyledSecondaryButton = styled(ButtonUnstyled)(({ theme }) => {
  return {
    alignItems: 'center',
    border: 'none',
    display: 'inline-flex',
    gap: 1,
    fontFamily: theme.typography.sans,
    fontWeight: 600,
    letterSpacing: '-0.75px',
    lineHeight: '130%',
    color: `${theme.palette['web3/component/button/secondary/text']} !important`,
    whiteSpace: 'nowrap',
    padding: 0,
    height: 'auto',
    borderRadius: '100px !important',
    overflow: 'hidden',
    textDecoration: 'none',
    outline: 'none',
    '--btn-surface-height': '48px',
    '--btn-surface-padding-x': '24px',
    '--btn-surface-width': '100%',
    [theme.breakpoints.up('xs')]: {
      fontSize: '1.8rem',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '2.0rem',
    },
    '& .btn-surface': {
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
      height: 'var(--btn-surface-height)',
      padding: '0 var(--btn-surface-padding-x)',
      borderRadius: 'inherit',
      width: 'var(--btn-surface-width)',
    },
    [theme.breakpoints.up('xl')]: {
      '--btn-surface-height': '56px',
      '& .btn-surface': {
        lineHeight: '150%',
      },
    },
    '&:focus-visible': {
      outlineWidth: '2px',
      outlineColor: theme.palette['web3/component/button/outline'],
      outlineStyle: 'solid',
      outlineOffset: '3px',
    },
    '& .bg-c': {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      borderRadius: 'inherit',
      '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        padding: '2px',
        borderRadius: 'inherit',
        mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        maskComposite: 'exclude',
        background:
          theme.palette['web3/component/button/secondary/resting-border'],
      },
    },
    '& .bg': {
      content: '""',
      position: 'absolute',
      top: '2px',
      left: '2px',
      right: '2px',
      bottom: '2px',
      background: 'transparent',
      borderRadius: 'inherit',
    },
    '& .txt': {
      position: 'relative',
    },
    '&:hover .bg-c, &:active .bg-c': {
      background:
        theme.palette['web3/component/button/secondary/interacted-fill'],
    },
    '&:hover .bg-c': {
      boxShadow: theme.palette['web3/component/button/secondary/box-shadow'],
    },
    '&:active .bg-c, &:active .txt': {
      boxShadow: 'none',
      transition: 'transform 0.1s ease',
      transform: 'scale(0.98)',
    },
    '&:hover .bg-c:before': {
      background:
        theme.palette['web3/component/button/secondary/interacted-border'],
    },
  };
});

const BtnSecondary = ({
  backgroundColor,
  children,
  defaultGap,
  iconName,
  iconSize,
  isInverted,
  leftIcon,
  rightIcon,
  sys,
  trackingId,
  variant,
  className,
  ...props
}: IBtnProps) => {
  const track = useTrack();
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });
  const { href, ...buttonProps } = props;
  const localizedHref = useLocalizedHref(href);

  const handleClick = (event) => {
    if (trackingId) {
      track({
        type: Events.BUTTON_CLICKED,
        payload: { trackingId, event, currentTarget: event.currentTarget },
      });
    }
    if (buttonProps.onClick && !href) {
      buttonProps.onClick(event);
    }
  };

  return (
    <StyledSecondaryButton
      {...buttonProps}
      href={localizedHref}
      slots={{ root: href ? 'a' : 'button' }}
      onClick={handleClick}
      className={cx(className, 'cms-btn-secondary')}
      {...inspectorProps({ fieldId: 'text' })}
    >
      <Box component='span' className='btn-surface'>
        <Box component='span' className='bg-c'>
          <Box component='span' className='bg' />
        </Box>
        <Stack
          gap={1.25}
          direction='row'
          component='span'
          className='txt'
          alignItems='center'
          justifyContent='center'
          sx={{
            '.btn-icon': {
              color: 'web3/component/button/secondary/text',
            },
            width: '100%',
          }}
        >
          {leftIcon} {children} {rightIcon}
        </Stack>
      </Box>
    </StyledSecondaryButton>
  );
};

export default BtnSecondary;
